import flatten from 'lodash/flattenDeep';
import uniqWith from 'lodash/uniqWith';
import intersectionWith from 'lodash/intersectionWith';
import differenceWith from 'lodash/differenceWith';

import { mutationTypes, actionTypes, errorTypes } from '../../../types';
import * as nativeInterface from '../../../tools/JS2NativeInterface';
import errorHandler from '../../../tools/errorHandler';

import { logger } from '../../../tools/logger';
import { PACKAGE_SYNCER_MODES, PACKAGE_SYNCER_ACTIONS, PACKAGE_SYNCER_STATE } from '../../../tools/consts';


function packageIntersection(array1, array2) {
	// generate whole packages index of datasource
	return intersectionWith(array1, array2, (x, y) => x.name === y.name && x.version === y.version);
}

function packageDifference(array1, array2) {
	return differenceWith(array1, array2, (x, y) => x.name === y.name && x.version === y.version);
}

function packageUniq(array) {
	return uniqWith(array, (x, y) => x.name === y.name && x.version === y.version);
}


export default {
	/**
	 * Init action for packageSyncer is needed to detect if app is an package Syncer app
	 * also gets current installed packages
	 * @param {*} store
	 */
	async [actionTypes.packageSyncerInit]({ commit, rootState }) {
		const { mode } = rootState.dataSourceCFG.configWebClient.packageSyncer;
		const initialPackages = rootState.dataSourceCFG.offlineAppWithVersionProviderInit;
		logger.log('PackageSyncer init in mode ' + mode);
		let packages = await nativeInterface.appGetOfflineAppsWithVersion(); // Does not support legacy mode anymore
		const packageSyncerSelectedSubParts = localStorage.packageSyncerSelectedSubParts
			? JSON.parse(localStorage.packageSyncerSelectedSubParts)
			: {};
		if (packages) {
			// an result means it is an package Syncer app
			logger.log('PackageSyncer packages on device: ', packages);
			let subParts = {};
			if (mode === PACKAGE_SYNCER_MODES.exhibition) {
				subParts = rootState.dataSourceCFG.exhibitions.reduce((acc, exhibition) => {
					const packagesGoal = packageUniq(exhibition.packagesWithVersion);
					const packagesInstalled = packageIntersection(packagesGoal, packages);

					acc[exhibition.exhibitionID] = {
						languages: [],
						packagesGoal,
						packagesInstalled,
						isSelected: !!packageSyncerSelectedSubParts[exhibition.exhibitionID],
					};
					return acc;
				}, {});
			}else if (mode === PACKAGE_SYNCER_MODES.tour) {
				subParts = Object.values(rootState.tour.toursMap).reduce((acc, tour) => {
					const packagesGoal = packageUniq(tour.packagesWithVersion);
					const packagesInstalled = packageIntersection(packagesGoal, packages);

					acc[tour.id] = {
						languages: [],
						packagesGoal,
						packagesInstalled,
						isSelected: !!packageSyncerSelectedSubParts[tour.id],
					};
					return acc;
				}, {});
			}else if (mode == PACKAGE_SYNCER_MODES.all) {
				const packagesGoal = packageUniq(
					flatten(rootState.dataSourceCFG.exhibitions.map(exhibition => exhibition.packagesWithVersion))
				);
				const packagesInstalled = packageIntersection(packagesGoal, packages);

				subParts[PACKAGE_SYNCER_MODES.all] = {
					languages: [],
					packagesGoal,
					packagesInstalled,
					isSelected: true,
				};
			}
			commit(mutationTypes.packageSyncerSetInstalledPackages, {
				packages,
			});
			commit(mutationTypes.packageSyncerSetIsSyncerApp, { mode, subParts, initialPackages });
			commit(mutationTypes.packageSyncerSetPackageGoal, { packages });
		} else {
			logger.log('PackageSyncer is not initialised');
		}
	},
	/**
	 * Starts Downloading all avaible packages
	 * @param {*} store
	 */
	async [actionTypes.packageSyncerDownloadAll]({ dispatch }) {
		logger.log('PackageSyncer download all');
		return dispatch(actionTypes.packageSyncerDownloadSubPart, { subPartID: PACKAGE_SYNCER_MODES.all });
	},
	/**
	 * Action downloads one subPart of all packages like one exhibition/tour
	 * if exhibition or tour is downloaded is defined by pacakgeSyncer.mode
	 * @param {*} store
	 * @param { subPartID }
	 */
	async [actionTypes.packageSyncerDownloadSubPart]({ state, commit, dispatch, getters }, { subPartID }) {
		// If the download was already started, do nothing;
		if (getters.packageSyncerPartState(subPartID) === PACKAGE_SYNCER_STATE.downloading) return;
		commit(mutationTypes.packageSyncerPushToSubPartActionQueue, {
			subPartID,
			action: PACKAGE_SYNCER_ACTIONS.download,
		});
		if (!state.isSyncing) return dispatch(actionTypes.packageSyncerStartSyncronisation);
	},
	async [actionTypes.packageSyncerDeleteSubPart]({ state, commit, dispatch, getters }, { subPartID }) {
		// If the download was started but is not ready yet, do nothing;
		if (getters.packageSyncerPartState(subPartID) === PACKAGE_SYNCER_STATE.downloading) return;
		commit(mutationTypes.packageSyncerPushToSubPartActionQueue, {
			subPartID,
			action: PACKAGE_SYNCER_ACTIONS.delete,
		});
		if (!state.isSyncing) return dispatch(actionTypes.packageSyncerStartSyncronisation);
	},

	async [actionTypes.packageSyncerStartSyncronisation]({ state, commit, dispatch }) {
		commit(mutationTypes.packageSyncerNextActionInQueue);
		logger.log('packageSyncer: packageSyncerStartSyncronisation');
		const packages = packageUniq(
			flatten(
				Object.values(state.subParts)
					.filter(subParts => subParts.isSelected)
					.map(subParts => subParts.packagesGoal)
			).concat(state.initialPackages)
		);
		commit(mutationTypes.packageSyncerSetPackageGoal, { packages });

		if (
			// check if packages already installed dont start native function, cause of long indexing then
			packageDifference(state.packagesGoal, state.packagesInstalled).length === 0 &&
			packageDifference(state.packagesInstalled, state.packagesGoal).length === 0
		) {
			return dispatch(actionTypes.packageSyncerStoppedSyncronisation);
		}
		nativeInterface.appStartOfflineAppSync(state.packagesGoal);
		commit(mutationTypes.packageSyncerSetSycronisationState, { isSyncing: true });
	},

	/**
	 * Action dispatchd when packageSyncer stopped syncronisation
	 * @param { * } store
	 */
	async [actionTypes.packageSyncerStoppedSyncronisation]({ state, commit, dispatch }) {
		logger.log('packageSyncer: packageSyncerStoppedSyncronisation');
		if (state.errors.length > 0) {
			state.errors.forEach(({ message }) => logger.error(message));
			if (state.currentAction.action === PACKAGE_SYNCER_ACTIONS.download) {
				errorHandler(new Error(errorTypes.packageSyncerDownloadError));
			} else {
				errorHandler(new Error(errorTypes.packageSyncerDeleteError));
			}
			return commit(mutationTypes.packageSyncerCancel);
		}
		commit(mutationTypes.packageSyncerSetInstalledPackages, {
			packages: state.packagesGoal,
		});

		if (state.actionQueue.length > 0) {
			dispatch(actionTypes.packageSyncerStartSyncronisation);
		} else {
			commit(mutationTypes.packageSyncerSetSycronisationState, { isSyncing: false });
		}
	},
};
